import { HiMiniUserGroup, HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import { LuLayoutDashboard } from 'react-icons/lu';
import { BsStars } from 'react-icons/bs';

import {
  ENABLE_INTEGRATION_GMAIL,
  ENABLE_INTEGRATION_GOOGLE_CALENDAR,
  ENABLE_INTEGRATION_GOOGLE_DRIVE,
  ENABLE_INTEGRATION_GOOGLE_DOCS,
  ENABLE_INTEGRATION_GOOGLE_SHEETS,
  ENABLE_INTEGRATION_GOOGLE_FORMS,
  ENABLE_INTEGRATION_GOOGLE_KEEP,
  ENABLE_INTEGRATION_GOOGLE_TASKS,
  ENABLE_INTEGRATION_GOOGLE_DIRECTORY,
  ENABLE_INTEGRATION_GOOGLE_VISION,
  ENABLE_INTEGRATION_YOUTUBE,
  ENABLE_INTEGRATION_MICROSOFT_OUTLOOK,
  ENABLE_INTEGRATION_MICROSOFT_TEAMS,
  ENABLE_INTEGRATION_MICROSOFT_EXCEL,
  ENABLE_INTEGRATION_MICROSOFT_ONEDRIVE,
  ENABLE_INTEGRATION_MICROSOFT_WORD,
  ENABLE_INTEGRATION_MICROSOFT_ONENOTE,
  ENABLE_INTEGRATION_MICROSOFT_SHAREPOINT,
  ENABLE_INTEGRATION_MICROSOFT_TODO,
  ENABLE_INTEGRATION_AZURE_DEVOPS,
  ENABLE_INTEGRATION_JIRA,
  ENABLE_INTEGRATION_CONFLUENCE,
  ENABLE_INTEGRATION_LINEAR,
  ENABLE_INTEGRATION_ASANA,
  ENABLE_INTEGRATION_NOTION,
  ENABLE_INTEGRATION_WRIKE,
  ENABLE_INTEGRATION_GITLAB,
  ENABLE_INTEGRATION_PIPEDRIVE,
  ENABLE_INTEGRATION_SLACK,
  ENABLE_INTEGRATION_GITHUB,
  ENABLE_INTEGRATION_ZOHO_CRM,
  ENABLE_INTEGRATION_GOHIGHLEVEL,
  ENABLE_INTEGRATION_HUBSPOT,
  ENABLE_INTEGRATION_INSTAGRAM,
  ENABLE_INTEGRATION_QUICKBOOKS,
  ENABLE_INTEGRATION_RAZORPAY,
  ENABLE_INTEGRATION_SHIPROCKET,
  ENABLE_INTEGRATION_ZENDESK,
  ENABLE_AGENT_MODE,
  ENABLE_REPORT_TEMPLATES,
} from '../config/features';
import {
  getEmailSuggestions,
  getSlackChannelSuggestions,
  getJiraProjectSuggestions,
  getJiraUserSuggestions,
  getJiraBoardSuggestions,
  getJiraSprintSuggestions,
  getConfluenceSpaceSuggestions,
  getGithubRepoSuggestions,
  getSlackUserSuggestions,
  getGithubCollaboratorSuggestions,
} from '../services/api';
import { getReportTemplateSuggestions } from '../services/reportTemplateApi';

export const RECENT_GOAL_COUNT = 4;

// Tools that require the user to upload a file before they can execute.
// The chat UI will pause and show a file-picker card for these functions.
export const functionsNeedingFileUpload = new Set([
  'import_orders_bulk',
  'bulk_import_products',
  'import_catalog_mappings',
]);

// Tools where file upload is needed only for a specific action argument.
// Key format: "toolName__actionValue"
export const functionsNeedingFileUploadForAction = new Set([
  'jira_manage_issue__attach_file', // only when action='attach_file'
]);

export const functionsNeedingApproval = new Set([
  'send_email_through_gmail',
  'reply_to_email_on_gmail',
  'send_message_in_thread_on_gmail',
  'create_google_calendar_event',
  'send_email_outlook',
  'reply_to_outlook_email',
  'forward_outlook_email',
  'create_calendar_event_outlook',
  'update_calendar_event',
  'delete_issue_in_jira',

  // "transition_status_of_jira_issue",
  'create_draft_email_outlook',
  'send_direct_message_to_user_microsoft_teams',
  'send_message_in_chat_microsoft_teams',
  'send_message_in_channel_microsoft_teams',
  'create_page_confluence',
  'update_page_confluence',
  'delete_page_confluence',
  'create_space_confluence',
  'delete_space_confluence',

  //jira (old atomic names — kept for backwards compat)
  'create_sprint_in_jira',
  'update_jira_issue',
  'add_comment_to_JIRA_issue',
  'update_comment_on_jira_issue',
  'add_watchers_to_issue_in_jira',
  'transition_status_of_jira_issue',
  'move_issues_to_jira_backlog',
  'move_specific_issues_to_backlog_of_specific_board_in_JIRA',
  'move_issues_from_backlog_to_specific_JIRA_board',
  'move_issues_to_epic_in_jira',
  'jira_rank_epics',
  'jira_rank_issues',
  'update_the_estimation_of_an_issue_for_a_specified_board_in_jira',
  'jira_update_sprint',
  'move_issues_to_sprint_and_rank_in_jira',
  'swap_jira_sprint',
  'remove_issues_from_epic_in_jira',
  'add_attachment_to_specific_issue_in_JIRA',
  'delete_issue_in_jira',

  // outlook
  'send_email_outlook',
  'reply_to_outlook_email',
  'forward_outlook_email',
  'create_draft_email_outlook',
  'update_email_message_outlook',
  'delete_email_message_outlook',
  'create_calendar_event_outlook',
  'update_event_outlook',
  'delete_event_outlook',
  'create_contact_outlook',
  'update_contact_outlook',
  'delete_contact_outlook',
  'update_user_profile_photo_outlook',
  'create_graph_subscription_outlook',
  'delete_graph_subscription_outlook',
  'add_commit_comment_outlook',
  'create_outlook_task',
  'update_outlook_task',
  'delete_outlook_task',
  'download_and_save_attachments_outlook',

  //github
  'create_github_repository_github',
  'update_github_repo_github',
  'transfer_repository_github',
  'create_branch_protection_github',
  'delete_branch_protection_github',
  'create_commit_github',
  'add_commit_comment_github',
  'create_pull_request_github',
  'update_pull_request_github',
  'merge_pull_request_github',
  'request_pull_request_review_github',
  'create_github_issue_github',
  'update_github_issue_github',
  'add_assignees_to_issue_github',
  'remove_assignees_from_issue_github',
  'create_label_github',
  'update_label_github',
  'delete_label_github',
  'create_milestone_github',
  'update_milestone_github',
  'create_release_github',
  'update_release_github',
  'create_fork_github',
  'merge_forks_github',
  'add_repository_collaborator_github',
  'create_repository_webhook_github',
  'update_repository_webhook_github',

  //teams
  'create_team_microsoft_teams',
  'create_channel_microsoft_teams',
  'add_member_to_team_microsoft_teams',
  'delete_channel_microsoft_teams',
  'send_message_in_channel_microsoft_teams',
  'send_direct_message_to_user_microsoft_teams',
  'send_message_in_chat_microsoft_teams',

  // microsoft_todo — a separate connected app from microsoft_outlook (both
  // operate on the same underlying Graph To Do data). Found live: GLM-5 chose
  // delete_task_from_microsoft_todo instead of outlook_manage_tasks to delete
  // an Outlook-created task, and it silently auto-executed since no
  // microsoft_todo write tool was ever added to this set.
  'create_task_list_in_microsoft_todo',
  'delete_task_list_from_microsoft_todo',
  'create_task_in_microsoft_todo',
  'update_task_in_microsoft_todo',
  'delete_task_from_microsoft_todo',
  'mark_task_as_completed_in_microsoft_todo',
  'undo_task_completion_in_microsoft_todo',
  'add_checklist_item_in_microsoft_todo',
]);

// Approval required only for specific write actions of compact tools.
// Key format: "toolName__actionValue"  (same pattern as functionsNeedingFileUploadForAction)
export const functionsNeedingApprovalForAction = new Set([
  // --- Confluence compact tool write actions ---
  // confluence_manage_pages: write actions only (get / history are reads)
  'confluence_manage_pages__create',
  'confluence_manage_pages__update',
  'confluence_manage_pages__delete',
  // confluence_manage_spaces: write actions only (list / detail are reads)
  'confluence_manage_spaces__create',
  'confluence_manage_spaces__delete',

  // --- Jira compact tool write actions ---
  // jira_manage_issue: write actions only (get_transitions / get_estimate are reads)
  'jira_manage_issue__create',
  'jira_manage_issue__update',
  'jira_manage_issue__delete',
  'jira_manage_issue__transition',
  'jira_manage_issue__set_estimate',
  'jira_manage_issue__add_watcher',
  // jira_manage_comments: write actions only (retrieve is a read)
  'jira_manage_comments__add',
  'jira_manage_comments__update',
  'jira_manage_comments__delete',
  // jira_manage_sprints: write actions only (get / list are reads)
  'jira_manage_sprints__create',
  'jira_manage_sprints__update',
  'jira_manage_sprints__swap',
  'jira_manage_sprints__delete',
  // jira_move_issues: all actions are writes
  'jira_move_issues__to_backlog',
  'jira_move_issues__to_backlog_on_board',
  'jira_move_issues__to_board',
  'jira_move_issues__to_sprint',
  'jira_move_issues__rank',
  // jira_manage_links: write actions only (list_types is a read)
  'jira_manage_links__create',
  'jira_manage_links__delete',
  // jira_manage_epics: all actions are writes
  'jira_manage_epics__set_parent',
  'jira_manage_epics__add_issues',
  'jira_manage_epics__remove_issues',
  'jira_manage_epics__rank',

  // --- Outlook compact tool write actions ---
  // outlook_manage_mail: write actions only (list / get / get_raw are reads)
  'outlook_manage_mail__send',
  'outlook_manage_mail__create_draft',
  'outlook_manage_mail__update',
  'outlook_manage_mail__delete',
  'outlook_manage_mail__forward',
  'outlook_manage_mail__reply',
  'outlook_manage_mail__download_attachments',
  'outlook_manage_mail__send_draft',
  'outlook_manage_mail__send_batch',
  // outlook_manage_calendar: write actions only (search is a read)
  'outlook_manage_calendar__create',
  'outlook_manage_calendar__update',
  'outlook_manage_calendar__delete',
  // outlook_manage_contacts: write actions only (list is a read)
  'outlook_manage_contacts__create',
  'outlook_manage_contacts__update',
  'outlook_manage_contacts__delete',
  // outlook_manage_folders: write actions only (list / get are reads)
  'outlook_manage_folders__create',
  'outlook_manage_folders__delete',
  // outlook_manage_tasks: all actions are writes
  'outlook_manage_tasks__create',
  'outlook_manage_tasks__update',
  'outlook_manage_tasks__delete',
  // outlook_manage_people has no write actions (list / search only) — no entries needed

  // --- Microsoft To Do compact tool write actions ---
  // todo_manage_lists: write actions only (list / get are reads)
  'todo_manage_lists__create',
  'todo_manage_lists__delete',
  // todo_manage_tasks: write actions only (list / get are reads)
  'todo_manage_tasks__create',
  'todo_manage_tasks__update',
  'todo_manage_tasks__delete',
  'todo_manage_tasks__complete',
  'todo_manage_tasks__undo_complete',
  // todo_manage_checklist: write actions only (list is a read)
  'todo_manage_checklist__add',
  'todo_manage_checklist__set_checked',
  'todo_manage_checklist__delete',

  // --- SharePoint write actions (sharepoint_files is reads only) ---
  'sharepoint_manage_items__create_folder',
  'sharepoint_manage_items__create_word',
  'sharepoint_manage_items__create_excel',
  'sharepoint_manage_items__create_powerpoint',
  'sharepoint_manage_items__update_word',
  'sharepoint_manage_items__rename',
  'sharepoint_manage_items__move',
  'sharepoint_manage_items__copy',
  'sharepoint_manage_items__delete',
  'sharepoint_manage_items__restore_version',
  // sharepoint_excel: list_sheets / read_range are reads
  'sharepoint_excel__update_range',
  'sharepoint_excel__add_sheet',
  'sharepoint_excel__add_table_rows',
  // sharepoint_sharing: find_people / list_access are reads
  'sharepoint_sharing__share_with_people',
  'sharepoint_sharing__create_link',
  'sharepoint_sharing__remove_access',
  // sharepoint_onenote: list_* / read_page are reads
  'sharepoint_onenote__create_page',

  // --- Microsoft Teams compact tool write actions ---
  // teams_manage_teams has no write actions (list / get / list_members only) — no entries needed
  // teams_manage_channels: write actions only (list is a read). 'create'/'delete' are
  // currently commented out of the backend tool schema (missing OAuth scope — see
  // teamsToolsGPT.py) but gated here anyway so approval works immediately once
  // that schema is uncommented, with no frontend change needed.
  'teams_manage_channels__create',
  'teams_manage_channels__delete',
  // teams_manage_channel_messages: write actions only (list / get / list_replies / search are reads)
  'teams_manage_channel_messages__send',
  'teams_manage_channel_messages__reply',
  // teams_manage_chats: write actions only (list / list_members / search are reads).
  // The whole teams_manage_chats tool is currently commented out of the backend
  // schema (missing Chat.Read* scope — see teamsToolsGPT.py); gated here so it's
  // ready the moment that tool is uncommented.
  'teams_manage_chats__send',
  'teams_manage_chats__send_direct',
  // teams_search_people has no write actions (search_users / search_people only) — no entries needed
]);

export const functionsNotNeedingApproval = new Set([
  'add_tools_to_task',
  'record_thought_process',
  'render_dynamic_react_component',
  'fetch_user_files',
  'get_current_datetime',
  'retrieve_index_content',
  'performCRUDOperationsOnUserFilesAndGenerateDataVisualizations',
  'read_webpage',
  'search_web',
]);

export const functionResponsesRequiringApproval = new Set([
  'search_emails_on_gmail',
  'list_inbox_emails_on_gmail',
  'list_messages_outlook',
  'search_issues_in_JIRA_independent_of_Agile',
  'advanced_jira_issue_search',
  'create_google_calendar_event',
  'create_calendar_event_outlook',
  'update_calendar_event',
  'performCRUDOperationsOnUserFilesAndGenerateDataVisualizations',
  'search_resources_for_module',
  'create_ticket',
]);

export const triggerOptions = [
  {
    id: 1,
    label: 'When I message the assistant',
    value: 'when_i_message_the_assistant',
    image: '/assets/web/oqlous.svg',
    isTrigger: true,
  },
  ...(ENABLE_INTEGRATION_GMAIL
    ? [
        {
          id: 2,
          label: 'Gmail',
          value: 'gmail',
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228561/mentorEducationImages/wbenqxucyj7mh0vsec5f.png',
          functions: [],
          isTrigger: true,
          triggerEvents: [
            {
              id: 1,
              event: 'messagesAdded',
              displayName: 'New Email Received',
              description:
                'Triggered when a new email arrives in your Gmail inbox or a label is applied.',
            },
            {
              id: 2,
              event: 'messagesDeleted',
              displayName: 'Email Deleted',
              description:
                'Triggered when an email is deleted from your Gmail account.',
            },
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_CALENDAR
    ? [
        {
          id: 3,
          label: 'Google Calendar',
          value: 'google_calendar',
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228690/mentorEducationImages/z9uxgo6owonunaeawede.png',
          function: [],
          isTrigger: true,
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_FORMS
    ? [
        {
          id: 4,
          label: 'Google Forms',
          value: 'google_forms',
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228723/mentorEducationImages/tlrf1bccjszfb6kg5dpp.png',
          function: [],
          isTrigger: true,
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_SHEETS
    ? [
        {
          id: 5,
          label: 'Google Sheets',
          value: 'google_sheets',
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228757/mentorEducationImages/fe7m1awjmmjgu975t31v.png',
          isTrigger: true,
        },
      ]
    : []),
  {
    id: 7,
    label: 'Slack',
    value: 'slack',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228824/mentorEducationImages/sb6gv5wfwjokccqrfwue.png',
    function: [],
    isTrigger: true,
  },
  ...(ENABLE_INTEGRATION_CONFLUENCE
    ? [
        {
          id: 10,
          label: 'Confluence',
          value: 'confluence',
          function: [],
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727456960/mentorEducationImages/e234hcpu3ovzzva45y8y.png',
          isTrigger: true,
        },
      ]
    : []),
  {
    id: 11,
    label: 'GitHub',
    value: 'github',
    functions: [],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727456981/mentorEducationImages/cxyxczli60shycbxq2zo.png',
    isTrigger: true,
    triggerEvents: [
      {
        id: 5,
        event: 'commit_comment',
        displayName: 'Commit Comment Added',
        description:
          'Triggered when a comment is added to a commit, enabling feedback or discussion on specific code changes.',
      },
      {
        id: 11,
        event: 'deployment',
        displayName: 'Deployment Event',
        description:
          'Triggered when a deployment is initiated, updated, or completed, marking the deployment of code to an environment.',
      },
      {
        id: 12,
        event: 'deployment_status',
        displayName: 'Deployment Status Changed',
        description:
          'Triggered when the status of a deployment changes (success, failure, or in-progress), tracking the deployment process.',
      },
      {
        id: 13,
        event: 'discussion',
        displayName: 'New Discussion',
        description:
          'Triggered when a new discussion is started or updated in the repository, enabling collaborative conversations.',
      },
      {
        id: 14,
        event: 'discussion_comment',
        displayName: 'Discussion Comment Added',
        description:
          'Triggered when a comment is added to an ongoing discussion, facilitating further conversation or clarification.',
      },
      {
        id: 31,
        event: 'pull_request',
        displayName: 'Pull Request Event',
        description:
          'Triggered when a pull request is opened, closed, merged, or synchronized, tracking code contributions from collaborators.',
      },
      {
        id: 32,
        event: 'pull_request_review_comment',
        displayName: 'PR Review Comment Added',
        description:
          'Triggered when a comment is added to a pull request review, enabling feedback on code changes.',
      },
      {
        id: 33,
        event: 'pull_request_review',
        displayName: 'Pull Request Reviewed',
        description:
          'Triggered when a pull request review is submitted, indicating whether the code is approved, rejected, or requires changes.',
      },
      {
        id: 34,
        event: 'pull_request_review_thread',
        displayName: 'PR Review Thread Updated',
        description:
          'Triggered when a comment thread is added or updated in a pull request review, facilitating detailed discussions.',
      },
      {
        id: 35,
        event: 'push',
        displayName: 'Code Pushed',
        description:
          'Triggered when code is pushed to a branch, indicating updates or new commits in the repository.',
      },
      {
        id: 37,
        event: 'release',
        displayName: 'Release Published',
        description:
          'Triggered when a release is created, published, updated, or deleted, marking a version of the project.',
      },
      {
        id: 2,
        event: 'check_run',
        displayName: 'Check Run Completed',
        description:
          'Triggered when a check run starts or completes, indicating the success or failure of tests or checks on a commit or pull request.',
      },
      {
        id: 3,
        event: 'check_suite',
        displayName: 'Check Suite Completed',
        description:
          'Triggered when a suite of checks starts or completes, including results from multiple checks for a commit or pull request.',
      },
      {
        id: 4,
        event: 'code_scanning_alert',
        displayName: 'Code Scan Alert',
        description:
          'Triggered when a new or updated code scanning alert highlights potential security vulnerabilities or coding issues in the repository.',
      },
      {
        id: 6,
        event: 'create',
        displayName: 'Resource Created',
        description:
          'Triggered when a new branch, tag, or repository is created, signaling a change in repository structure or versioning.',
      },
      {
        id: 9,
        event: 'dependabot_alert',
        displayName: 'Dependabot Alert',
        description:
          'Triggered when a new alert is generated by Dependabot, indicating security issues in project dependencies.',
      },
      {
        id: 16,
        event: 'gollum',
        displayName: 'Wiki Page Updated',
        description:
          'Triggered when a wiki page is created, updated, or deleted, helping manage the project’s documentation hosted on GitHub.',
      },
      {
        id: 17,
        event: 'issue_comment',
        displayName: 'Issue Comment Added',
        description:
          'Triggered when a comment is added to an issue, facilitating discussion or updates on tasks, bugs, or features.',
      },
      {
        id: 18,
        event: 'issues',
        displayName: 'Issue Updated',
        description:
          'Triggered when an issue is created, updated, closed, or reopened, tracking tasks, bugs, or enhancements in the project.',
      },
      {
        id: 19,
        event: 'label',
        displayName: 'Label Updated',
        description:
          'Triggered when a label is created, updated, or deleted, helping categorize and organize issues or pull requests.',
      },
      {
        id: 20,
        event: 'member',
        displayName: 'Member Added',
        description:
          'Triggered when a user is added to a repository or organization, granting them access to the project.',
      },
      {
        id: 38,
        event: 'repository_advisory',
        displayName: 'Repository Security Advisory',
        description:
          'Triggered when a security advisory is created or updated for a repository, alerting users to potential vulnerabilities.',
      },
      {
        id: 1,
        event: 'branch_protection_rule',
        displayName: 'Branch Protection',
        description:
          'Triggered when a branch protection rule is added, updated, or removed to enforce specific permissions and status checks on branches.',
      },
      {
        id: 7,
        event: 'custom_property_values',
        displayName: 'Custom Property Update',
        description:
          'Triggered when custom properties or settings are added or updated in the repository for specific configurations.',
      },
      {
        id: 8,
        event: 'delete',
        displayName: 'Resource Deleted',
        description:
          'Triggered when a branch, tag, or repository is deleted, indicating the removal of a resource.',
      },
      {
        id: 10,
        event: 'deploy_key',
        displayName: 'Deploy Key Updated',
        description:
          'Triggered when a deploy key is added or removed, controlling access to deploy the repository to external environments.',
      },
      {
        id: 15,
        event: 'fork',
        displayName: 'Repository Forked',
        description:
          'Triggered when a repository is forked, allowing users to create their own copies of the repository for modifications.',
      },
      {
        id: 21,
        event: 'merge_group',
        displayName: 'Merge Group Event',
        description:
          'Triggered when multiple pull requests are merged together, consolidating changes into a target branch.',
      },
      {
        id: 22,
        event: 'meta',
        displayName: 'Repository Metadata Updated',
        description:
          'Triggered for repository-level changes, such as creation or deletion, impacting the entire repository.',
      },
      {
        id: 23,
        event: 'milestone',
        displayName: 'Milestone Event',
        description:
          'Triggered when a milestone is created, updated, or closed, tracking project goals or releases.',
      },
      {
        id: 24,
        event: 'package',
        displayName: 'Package Published',
        description:
          'Triggered when a package is published, updated, or deleted in the GitHub Package Registry, managing dependencies and versions.',
      },
      {
        id: 25,
        event: 'page_build',
        displayName: 'Page Build Event',
        description:
          'Triggered when a GitHub Pages site is built or updated, managing the project’s website or documentation.',
      },
      {
        id: 26,
        event: 'ping',
        displayName: 'Ping Event',
        description:
          'Triggered as a simple connection check or to test the status of an integration.',
      },
      {
        id: 27,
        event: 'project_card',
        displayName: 'Project Card Updated',
        description:
          'Triggered when a project card is created, updated, moved, or deleted, managing tasks within a project board.',
      },
      {
        id: 28,
        event: 'project',
        displayName: 'Project Updated',
        description:
          'Triggered when a project is created, updated, or deleted, marking changes in the project board or management structure.',
      },
      {
        id: 29,
        event: 'project_column',
        displayName: 'Project Column Updated',
        description:
          'Triggered when a column is created, updated, or deleted within a project board, organizing tasks or issues.',
      },
      {
        id: 30,
        event: 'public',
        displayName: 'Repository Made Public',
        description:
          'Triggered when a private repository is made public, making its content accessible to all users.',
      },
      {
        id: 36,
        event: 'registry_package',
        displayName: 'Registry Package Updated',
        description:
          'Triggered when a package in the GitHub Registry is created, updated, or deleted.',
      },
      {
        id: 39,
        event: 'repository',
        displayName: 'Repository Updated',
        description:
          'Triggered when a repository is created, updated, or deleted, indicating changes at the repository level.',
      },
      {
        id: 40,
        event: 'repository_import',
        displayName: 'Repository Imported',
        description:
          'Triggered when a repository is imported from another source, integrating external codebases into GitHub.',
      },
      {
        id: 41,
        event: 'repository_ruleset',
        displayName: 'Repository Ruleset Updated',
        description:
          'Triggered when a repository’s ruleset is created or modified, defining governance policies for the project.',
      },
      {
        id: 42,
        event: 'repository_vulnerability_alert',
        displayName: 'Vulnerability Alert',
        description:
          'Triggered when a vulnerability is detected in the repository, notifying you about potential security issues.',
      },
      {
        id: 43,
        event: 'secret_scanning_alert',
        displayName: 'Secret Scanning Alert',
        description:
          'Triggered when a secret scanning alert is generated, indicating sensitive data or credentials found in the codebase.',
      },
      {
        id: 44,
        event: 'secret_scanning_alert_location',
        displayName: 'Secret Alert Location Updated',
        description:
          'Triggered when the location of a secret scanning alert is updated, indicating where sensitive data was detected.',
      },
      {
        id: 45,
        event: 'secret_scanning_scan',
        displayName: 'Secret Scan Completed',
        description:
          'Triggered when a secret scanning scan is completed, checking the codebase for exposed secrets or sensitive information.',
      },
      {
        id: 46,
        event: 'security_and_analysis',
        displayName: 'Security & Analysis Updated',
        description:
          'Triggered when a security analysis or update occurs on the repository, alerting users about potential security risks.',
      },
    ],
  },
  ...(ENABLE_INTEGRATION_MICROSOFT_TEAMS
    ? [
        {
          id: 15,
          label: 'Teams',
          value: 'teams',
          function: [],
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727457135/mentorEducationImages/vr3wpu2bzwdhyqd9hq19.png',
          isTrigger: true,
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_TASKS
    ? [
        {
          id: 16,
          label: 'Google Tasks',
          value: 'google_tasks',
          function: [],
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727457121/mentorEducationImages/thywjdnrzkhqtp1caxrk.png',
          isTrigger: true,
        },
      ]
    : []),
  {
    id: 17,
    label: 'Trello',
    value: 'trello',
    function: [],
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727457282/mentorEducationImages/ytyephudun2ew1w526jk.png',
    isTrigger: true,
  },
  ...(ENABLE_INTEGRATION_JIRA
    ? [
        {
          id: 18,
          label: 'Jira',
          value: 'jira',
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228864/mentorEducationImages/x3hajcpo5sbpdb0klxvu.png',
          functions: [],
          isTrigger: true,
          triggerEvents: [
            {
              id: 1,
              event: 'issueCreated',
              displayName: 'Issue Created',
              description: 'Triggers when a new issue is created.',
            },
            {
              id: 2,
              event: 'issueUpdated',
              displayName: 'Issue Updated',
              description: 'Triggers when an issue is changed.',
            },
            {
              id: 3,
              event: 'issueDeleted',
              displayName: 'Issue Deleted',
              description: 'Triggers when an issue is deleted.',
            },
            {
              id: 4,
              event: 'commentAdded',
              displayName: 'Comment Added',
              description: 'Triggers when a comment is added.',
            },
            {
              id: 7,
              event: 'issuePropertySet',
              displayName: 'Custom Data Changed',
              description: 'Triggers when custom data is set or updated.',
            },
            {
              id: 5,
              event: 'commentEdited',
              displayName: 'Comment Edited',
              description: 'Triggers when a comment is updated.',
            },
            {
              id: 6,
              event: 'commentDeleted',
              displayName: 'Comment Deleted',
              description: 'Triggers when a comment is removed.',
            },
            {
              id: 8,
              event: 'issuePropertyRemoved',
              displayName: 'Custom Data Removed',
              description: 'Triggers when custom data is deleted.',
            },
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_OUTLOOK
    ? [
        {
          id: 19,
          label: 'Outlook',
          value: 'microsoft_outlook',
          image: '#',
          functions: [],
          isTrigger: true,
          triggerEvents: [
            {
              id: 1,
              event: 'created',
              displayName: 'New Email Received',
              description:
                'Triggered when a new email is received in your inbox.',
            },
            {
              id: 2,
              event: 'updated',
              displayName: 'Email Updated',
              description:
                'Triggered when an email is flagged, read, moved, or otherwise modified.',
            },
            {
              id: 3,
              event: 'deleted',
              displayName: 'Email Deleted',
              description:
                'Triggered when an email is deleted from the mailbox.',
            },
            {
              id: 4,
              event: 'missed',
              displayName: 'Missed Notification',
              description:
                'Triggered when an email-related notification is missed due to being offline or unreachable.',
            },
          ],
        },
      ]
    : []),
];

export const triggerOptionsNew = [
  {
    id: 1,
    value: 'workflow_executor',
    label: 'Workflow Executor',
    image: '/assets/web/oqlous.svg',
    parameters: [
      { label: 'Input Data', param: 'inputData', type: 'textarea', value: '' },
    ],
  },

  ...(ENABLE_INTEGRATION_JIRA
    ? [
        {
          id: 2,
          value: 'jira',
          label: 'Jira',

          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726228864/mentorEducationImages/x3hajcpo5sbpdb0klxvu.png',
          triggerEvents: [
            'jira:issue_created',
            'jira:issue_updated',
            'jira:issue_deleted',
            'jira:issue_transitioned',
          ],
          parameters: [
            {
              label: 'Exclude Body of the Issue',
              param: 'excludeBody',
              type: 'text',
              required: false,
              value: false,
            },
            {
              label: 'Webhook Name',
              param: 'name',
              type: 'text',
              required: true,
              value: 'MyWebhook',
            },
            {
              label: 'Filter (JQL Query)',
              param: 'filter',
              type: 'text',
              required: false,
              value: "project = 'YourProject'",
            },
          ],
        },
      ]
    : []),

  ...(ENABLE_INTEGRATION_GITHUB
    ? [
        {
          id: 3,
          value: 'github',
          label: 'GitHub',
          triggerEvents: [
            'push',
            'pull_request',
            'issues',
            'issue_comment',
            'create',
            'delete',
            'release',
          ],
          parameters: [
            {
              label: 'Repository Name',
              param: 'REPO',
              type: 'text',
              required: true,
              value: '',
            },
          ],
          image:
            'https://res.cloudinary.com/dsguoq6ad/image/upload/v1727456981/mentorEducationImages/cxyxczli60shycbxq2zo.png',
        },
      ]
    : []),
];

export const authApps = [
  ...(ENABLE_INTEGRATION_JIRA ? ['jira'] : []),
  ...(ENABLE_INTEGRATION_GMAIL ? ['gmail'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_CALENDAR ? ['google_calendar'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DRIVE ? ['google_drive'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_FORMS ? ['google_forms'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_KEEP ? ['google_keep'] : []),
  // "cloud-platform",
  ...(ENABLE_INTEGRATION_GOOGLE_DOCS ? ['google_docs'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_SHEETS ? ['google_sheets'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_TASKS ? ['google_tasks'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_VISION ? ['google_vision'] : []),
  ...(ENABLE_INTEGRATION_YOUTUBE ? ['youtube'] : []),
  ...(ENABLE_INTEGRATION_LINEAR ? ['linear'] : []),
  ...(ENABLE_INTEGRATION_GITHUB ? ['github'] : []),
  ...(ENABLE_INTEGRATION_ASANA ? ['asana'] : []),
  ...(ENABLE_INTEGRATION_CONFLUENCE ? ['confluence'] : []),
  ...(ENABLE_INTEGRATION_NOTION ? ['notion'] : []),
  ...(ENABLE_INTEGRATION_WRIKE ? ['wrike'] : []),
  ...(ENABLE_INTEGRATION_GITLAB ? ['gitlab'] : []),
  ...(ENABLE_INTEGRATION_PIPEDRIVE ? ['pipedrive'] : []),
  ...(ENABLE_INTEGRATION_SLACK ? ['slack'] : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_TEAMS ? ['microsoft_teams'] : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_OUTLOOK ? ['microsoft_outlook'] : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_EXCEL ? ['microsoft_excel'] : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_ONEDRIVE ? ['microsoft_onedrive'] : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_WORD ? ['microsoft_word'] : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_ONENOTE ? ['microsoft_onenote'] : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_TODO ? ['microsoft_todo'] : []),
  ...(ENABLE_INTEGRATION_AZURE_DEVOPS ? ['azure_devops'] : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DIRECTORY ? ['google_directory'] : []),
  ...(ENABLE_INTEGRATION_ZENDESK ? ['zendesk'] : []),
  ...(ENABLE_INTEGRATION_ZOHO_CRM ? ['zoho_crm'] : []),
  ...(ENABLE_INTEGRATION_SHIPROCKET ? ['shiprocket'] : []),
  ...(ENABLE_INTEGRATION_RAZORPAY ? ['razorpay'] : []),
];

export const appImages = {
  pipedrive: '/assets/icons/pipedrive_icon.png',
  gitlab: '/assets/icons/gitlab_icon.png',
  wrike: '/assets/icons/wrike_icon.png',
  notion: '/assets/icons/notion_icon.png',
  asana: '/assets/icons/asana_icon.png',
  jira: '/assets/icons/jira_icon.png',
  google_calendar: '/assets/icons/google_calandar_icon.png',
  gmail: '/assets/icons/gmail_icon.png',
  google_keep: '/assets/icons/google_keep.png',
  google_directory: '/assets/icons/google_directory.png',
  google_forms: '/assets/icons/google_form.png',
  confluence: '/assets/icons/confluence.png',
  github: '/assets/icons/github_icon.png',
  linear: '/assets/icons/linear.png',
  google_tasks: '/assets/icons/google_task.png',
  trello: '/assets/icons/trello_icon.png',
  slack: '/assets/icons/slack_icon.svg',
  microsoft_excel: '/assets/icons/excel_icon.png',
  google_sheets: '/assets/icons/google_sheet.png',
  google_docs: '/assets/icons/google_docs_icon.png',
  google_drive: '/assets/icons/google_drive_icon.png',
  microsoft_outlook: '/assets/icons/outlook.png',
  microsoft_onedrive: '/assets/icons/onedrive_icon.png',
  microsoft_onenote: '/assets/icons/onenote_icon.png',
  microsoft_todo: '/assets/icons/ms_todo_icon.png',
  microsoft_teams: '/assets/icons/teams_icon.png',
  zoho_book: '/assets/icons/zoho_book_icon.png',
  zoho_cliq: '/assets/icons/zoho_cliq_icon.jpg',
  zoho_crm: '/assets/icons/zoho_crm.png',
  azure_devops: '/assets/icons/azure_developer_icon.webp',
  microsoft_word: '/assets/icons/ms_words_icon.avif',
  zendesk: '/assets/icons/zendesk_icon.svg',
  shiprocket: '/assets/icons/shiprocket_logo.png',
  razorpay: '/assets/icons/razorpay.png',
  agent: '/assets/logo.png',
  workflow: '/assets/logo.png',
  misc: '/assets/logo.png',
};

// FULLY DETAILED, MANAGER-FOCUSED STATIC DATA ---

export const notifications = [];

export const waitingEvents = [];

export const integrationData = [
  ...(ENABLE_INTEGRATION_MICROSOFT_EXCEL
    ? [
        {
          id: 23,
          label: 'Excel',
          value: 'microsoft_excel',
          image: '/assets/icons/excel_icon.png',
          functions: [
            'MANAGE Workbooks',
            'MANAGE Worksheets',
            'MANAGE Tables & Ranges',
            'MANAGE Comments',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_WORD
    ? [
        {
          id: 25,
          label: 'Word',
          value: 'microsoft_word',
          image: '/assets/icons/ms_words_icon.avif',
          functions: [
            'MANAGE Documents',
            'MANAGE Version History',
            'MANAGE Permissions',
            'MANAGE Comments',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_ONENOTE
    ? [
        {
          id: 27,
          label: 'OneNote',
          value: 'microsoft_onenote',
          image: '/assets/icons/onenote_icon.png',
          functions: [
            'MANAGE Notebooks',
            'MANAGE Sections',
            'MANAGE Pages',
            'MANAGE Content',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_AZURE_DEVOPS
    ? [
        {
          id: 38,
          label: 'Azure DevOps',
          value: 'azure_devops',
          image: '/assets/icons/azure_developer_icon.webp',
          functions: [
            'MANAGE User & Organizations',
            'MANAGE Projects',
            'MANAGE Work Items',
            'MANAGE Builds & Pipelines',
            'MANAGE Releases & Deployments',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_JIRA
    ? [
        {
          id: 1,
          label: 'Jira',
          value: 'jira',
          image: '/assets/icons/jira_icon.png',
          functions: [
            'MANAGE Issues',
            'MANAGE Comments',
            'MANAGE Jira Projects',
            'MANAGE Users',
            'MANAGE Boards',
            'MANAGE Sprints',
            'MANAGE Epics',
            'MANAGE Backlog',
            'MANAGE Versions',
            'REPORTS',
            'SETTINGS',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_CALENDAR
    ? [
        {
          id: 2,
          label: 'Google Calendar',
          value: 'google_calendar',
          image: '/assets/icons/google_calandar_icon.png',
          functions: [
            'MANAGE Events',
            'MANAGE Calendars',
            'MANAGE Google Meet',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GMAIL
    ? [
        {
          id: 3,
          label: 'Gmail',
          value: 'gmail',
          image: '/assets/icons/gmail_icon.png',
          functions: [
            'MANAGE Emails',
            'MANAGE Drafts',
            'MANAGE Labels',
            'MANAGE Threads',
            'MANAGE Filters',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_FORMS
    ? [
        {
          id: 7,
          label: 'Google Forms',
          value: 'google_forms',
          image: '/assets/icons/google_form.png',
          functions: ['Form Management', 'FORM Responses'],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_KEEP
    ? [
        {
          id: 6,
          label: 'Google Keep',
          value: 'google_keep',
          image: '/assets/icons/google_keep.png',
          functions: ['Note Management'],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DIRECTORY
    ? [
        {
          id: 30,
          label: 'Google Directory',
          value: 'google_directory',
          image: '/assets/icons/google_directory.png',
          functions: ['Directory Search', 'User Management'],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_VISION
    ? [
        {
          id: 31,
          label: 'Google Vision',
          value: 'google_vision',
          image: '/assets/icons/google_vision.png',
          functions: ['Image Analysis', 'Text Extraction (OCR)'],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_CONFLUENCE
    ? [
        {
          id: 11,
          label: 'Confluence',
          value: 'confluence',
          function: [],
          image: '/assets/icons/confluence.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_LINEAR
    ? [
        {
          id: 40,
          label: 'Linear',
          value: 'linear',
          image: '/assets/icons/linear.png',
          functions: ['MANAGE Users', 'MANAGE Teams', 'MANAGE Issues'],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_ASANA
    ? [
        {
          id: 41,
          label: 'Asana',
          value: 'asana',
          image: '/assets/icons/asana_icon.png',
          functions: [
            'MANAGE Projects',
            'MANAGE Tasks',
            'MANAGE Subtasks',
            'MANAGE Teams & Users',
            'MANAGE Comments & Attachments',
            'MANAGE Sections & Tags',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_NOTION
    ? [
        {
          id: 42,
          label: 'Notion',
          value: 'notion',
          image: '/assets/icons/notion_icon.png',
          functions: [
            'MANAGE Users',
            'MANAGE Databases',
            'MANAGE Pages',
            'MANAGE Blocks',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_WRIKE
    ? [
        {
          id: 43,
          label: 'Wrike',
          value: 'wrike',
          image: '/assets/icons/wrike_icon.png',
          functions: [
            'MANAGE Projects',
            'MANAGE Tasks',
            'MANAGE Folders',
            'MANAGE Workspaces',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GITLAB
    ? [
        {
          id: 44,
          label: 'GitLab',
          value: 'gitlab',
          image: '/assets/icons/gitlab_icon.png',
          functions: [
            'MANAGE Repositories',
            'MANAGE Branches',
            'MANAGE Files',
            'MANAGE Commits',
            'MANAGE Issues & Merge Requests',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_PIPEDRIVE
    ? [
        {
          id: 45,
          label: 'Pipedrive',
          value: 'pipedrive',
          image: '/assets/icons/pipedrive_icon.png',
          functions: [
            'MANAGE Deals',
            'MANAGE Persons',
            'MANAGE Organizations',
            'MANAGE Activities',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GITHUB
    ? [
        {
          id: 12,
          label: 'GitHub',
          value: 'github',
          function: [],
          image: '/assets/icons/github_icon.png',
          functions: [
            'MANAGE Branches',
            'MANAGE Commits',
            'MANAGE Issues',
            'MANAGE Pull Requests',
            'MANAGE Labels',
            'MANAGE Milestones',
            'MANAGE Releases',
            'MANAGE Repositories',
            'MANAGE Forks',
            'MANAGE Webhooks',
            'MANAGE Collaborators',
            'MANAGE User Information',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_TEAMS
    ? [
        {
          id: 14,
          label: 'Teams',
          value: 'microsoft_teams',

          image: '/assets/icons/teams_icon.png',
          functions: [
            'MANAGE Teams & Groups',
            'MANAGE Channels',
            'MANAGE Channel Messages',
            'MANAGE Chats & Messages',
            'MANAGE Members & Users',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_TASKS
    ? [
        {
          id: 15,
          label: 'Google Tasks',
          value: 'google_tasks',
          function: [],
          image: '/assets/icons/google_task.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_SLACK
    ? [
        {
          id: 17,
          label: 'Slack',
          value: 'slack',
          function: [],
          image: '/assets/icons/slack_icon.svg',
          functions: [
            'MANAGE Messages',
            'MANAGE Reactions',
            'MANAGE Channels',
            'MANAGE Files',
            'MANAGE Reminders',
            'MANAGE Team',
            'MANAGE Users',
            'MANAGE User Groups',
            'MANAGE Views',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_SHEETS
    ? [
        {
          id: 19,
          label: 'Google Sheets',
          value: 'google_sheets',
          image: '/assets/icons/google_sheet.png',
          functions: ['Manage Sheets'],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DOCS
    ? [
        {
          id: 20,
          label: 'Google Docs',
          value: 'google_docs',
          image: '/assets/icons/google_docs_icon.png',
          functions: [
            'CREATE Documents',
            'UPDATE Documents',
            'SEARCH Documents',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DRIVE
    ? [
        {
          id: 21,
          label: 'Google Drive',
          value: 'google_drive',
          image: '/assets/icons/google_drive_icon.png',
          functions: [
            'FILE Operations',
            'FILE Metadata',
            'FOLDER Operations',
            'SEARCH Operations',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_OUTLOOK
    ? [
        {
          id: 22,
          label: 'Outlook',
          value: 'microsoft_outlook',
          image: '/assets/icons/outlook.png',
          functions: [
            'MANAGE Emails',
            'MANAGE Calendar Events',
            'MANAGE Contacts',
            'MANAGE Mail Folders',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_ONEDRIVE
    ? [
        {
          id: 24,
          label: 'OneDrive',
          value: 'microsoft_onedrive',
          image: '/assets/icons/onedrive_icon.png',
          functions: [],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_TODO
    ? [
        {
          id: 26,
          label: 'Todo',
          value: 'microsoft_todo',
          image: '/assets/icons/ms_todo_icon.png',
          functions: ['MANAGE Tasks'],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_SHAREPOINT
    ? [
        {
          id: 47,
          label: 'SharePoint',
          value: 'microsoft_sharepoint',
          image: '/assets/icons/onedrive_icon.png',
          functions: [
            'READ approved SharePoint sites',
            'READ OneDrive (optional)',
            'Word, Excel, PowerPoint, PDF',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_ZOHO_CRM
    ? [
        {
          id: 29,
          label: 'Zoho CRM',
          value: 'zoho_crm',
          image: '/assets/icons/zoho_crm.png',
          functions: [
            'MANAGE Leads',
            'MANAGE Contacts',
            'MANAGE Accounts',
            'MANAGE Deals',
            'MANAGE Tasks',
            'MANAGE Events',
            'MANAGE Notes',
            'MANAGE Custom Modules',
            'MANAGE Users',
            'MANAGE Mass Update',
            'MANAGE Attachments',
            'MANAGE Bulk Operations',
            'MANAGE Fields & Layouts',
            'MANAGE Tags',
            'MANAGE Related Lists',
            'MANAGE Modules',
            'MANAGE Roles & Profiles',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_HUBSPOT
    ? [
        {
          id: 33,
          label: 'HubSpot',
          value: 'hubspot',
          image:
            'https://i.pinimg.com/736x/90/83/b0/9083b0faecc8b67b257be30857dac6c1.jpg',
          functions: [
            'MANAGE CRM Objects',
            'MANAGE Associations',
            'MANAGE Properties',
            'MANAGE Schemas',
            'MANAGE Workflows',
            'MANAGE Engagements',
            'MANAGE User Details & Links',
          ],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOHIGHLEVEL
    ? [
        {
          id: 34,
          label: 'GoHighLevel',
          value: 'gohighlevel',
          image:
            'https://cdn.prod.website-files.com/5f15081919fdf673994ab5fd/6697e68b90253f000eed3a7f_HighLevel-Logo-(PNG)-p-500.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_QUICKBOOKS
    ? [
        {
          id: 35,
          label: 'QuickBooks',
          value: 'quickbooks',
          image: '/assets/icons/quickbooks.png',
          functions: [],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_INSTAGRAM
    ? [
        {
          id: 36,
          label: 'Instagram',
          value: 'instagram',
          image: '/assets/icons/instagram_icon.jpg',
          functions: [],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_YOUTUBE
    ? [
        {
          id: 37,
          label: 'Youtube',
          value: 'youtube',
          image: '/assets/icons/youtube_icon.png',
          functions: [],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_SHIPROCKET
    ? [
        {
          id: 38,
          label: 'Shiprocket',
          value: 'shiprocket',
          image: '/assets/icons/shiprocket_logo.png',
          functions: [],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_RAZORPAY
    ? [
        {
          id: 39,
          label: 'Razorpay',
          value: 'razorpay',
          image: '/assets/icons/razorpay.png',
          functions: [],
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_ZENDESK
    ? [
        {
          id: 40,
          label: 'Zendesk',
          value: 'zendesk',
          image: '/assets/icons/zendesk_icon.svg',
          functions: ['READ Tickets', 'CREATE Tickets', 'UPDATE Tickets'],
        },
      ]
    : []),
];

export const azureDevopsImage = integrationData.find(
  (integration) => integration.value === 'azure_devops',
)?.image;

export const dataSources = [
  {
    id: 4,
    label: 'Google Drive',
    value: 'google_drive',
    image:
      'https://res.cloudinary.com/dsguoq6ad/image/upload/v1726229007/mentorEducationImages/lhupskidmwtibynwa0no.png',
    functions: ['File Management', 'Folder Management'],
  },
];

export const questions = [
  { id: 1, question: 'What is your name?' },
  { id: 2, question: 'What is your favorite color?' },
  { id: 3, question: 'What is your hobby?' },
  { id: 4, question: 'What is your favorite food?' },
];

export const customDropdownStyle = {
  control: (provided, state) => ({
    ...provided,
    border: 'none',
    backgroundColor: '#27272a',
    boxShadow: 'none',
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#333',
    color: '#fff',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#444' : '#333',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#555',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#fff',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#aaa',
  }),
  input: (provided) => ({
    ...provided,
    color: '#fff',
  }),
};

export const topTabs = [
  {
    icon: HiOutlineChatBubbleLeftRight,
    label: 'Chat',
    link: '/chat',
    entityId: 'chat',
  },
  {
    icon: LuLayoutDashboard,
    label: 'Analytics',
    link: '/analytics/insights',
    // Analytics has 5 sub-pages under /analytics/*, all still "the
    // Analytics tab" for highlighting purposes even though the tab itself
    // navigates to one specific section (Insights).
    matchPrefix: '/analytics',
    entityId: 'project-register',
  },
  {
    icon: HiMiniUserGroup,
    label: 'Communication',
    link: '/communication',
    entityId: 'communication',
  },
  {
    icon: BsStars,
    label: 'Workflow',
    link: '/workflow',
    entityId: 'workflow',
  },
];

export const dialogueData = [];

export const meetingFeedbackData = [];

//Auto-complete trigger configurations for different data types
export const triggers = {
  '@mail': {
    // data: emails,
    data: getEmailSuggestions,
    mainField: 'email',
    displayFields: ['name', 'TimeZone'],
    type: 'mail',
  },
  ...(ENABLE_INTEGRATION_SLACK
    ? {
        '@slackuser': {
          data: getSlackUserSuggestions,
          mainField: 'UserName',
          displayFields: ['RealName', 'TimeZone'],
          type: 'slack-user',
        },
        '@slackchannel': {
          data: getSlackChannelSuggestions,
          mainField: 'name',
          displayFields: [],
          type: 'slack-channel',
        },
      }
    : {}),
  ...(ENABLE_INTEGRATION_JIRA
    ? {
        '@jirasprint': {
          data: getJiraSprintSuggestions,
          mainField: 'name',
          displayFields: ['state'],
          type: 'jira-sprint',
        },
        '@jiraboard': {
          data: getJiraBoardSuggestions,
          mainField: 'name',
          displayFields: ['type'],
          type: 'jira-board',
        },
        '@jiraproject': {
          data: getJiraProjectSuggestions,
          mainField: 'name',
          displayFields: ['key'],
          type: 'jira-project',
        },
        '@jirauser': {
          data: getJiraUserSuggestions,
          mainField: 'name',
          displayFields: [],
          type: 'jira-user',
        },
      }
    : {}),
  ...(ENABLE_INTEGRATION_GITHUB
    ? {
        '@collab': {
          data: getGithubCollaboratorSuggestions,
          mainField: 'name',
          displayFields: [],
          type: 'collaborator',
        },
        '@repo': {
          data: getGithubRepoSuggestions,
          mainField: 'name',
          displayFields: [],
          type: 'repository',
        },
      }
    : {}),
  ...(ENABLE_INTEGRATION_CONFLUENCE
    ? {
        '@space': {
          data: getConfluenceSpaceSuggestions,
          mainField: 'name',
          displayFields: ['key'],
          type: 'confluence-space',
        },
      }
    : {}),
  ...(ENABLE_REPORT_TEMPLATES
    ? {
        '@template': {
          data: getReportTemplateSuggestions,
          mainField: 'template_name',
          displayFields: ['template_type', 'section_count'],
          type: 'report-template',
        },
        '@': {
          data: getReportTemplateSuggestions,
          mainField: 'template_name',
          displayFields: ['template_type', 'section_count'],
          type: 'report-template',
        },
      }
    : {}),
  '/': {
    data: [], // populated dynamically in AutoResizableTextarea
    mainField: 'command',
    displayFields: ['description'],
    type: 'slash-command',
  },
};

// Slash command definitions — /cmd enables, /cmd-off disables
// action: 'toggle-bool' | 'toggle-integration' | 'open-panel'
// stateKey: used for toggle-bool actions
// appname: used for toggle-integration actions (must match oqlousTools appname from useMCP)
// panelType: used for open-panel actions
export const slashCommands = [
  // ── AI Features ──────────────────────────────────────────
  {
    command: '/web',
    offCommand: '/web-off',
    label: 'Web Search',
    description: 'Toggle real-time web search',
    action: 'toggle-bool',
    stateKey: 'isWebSearch',
    icon: null,
  },
  {
    command: '/reason',
    offCommand: '/reason-off',
    label: 'Medium effort',
    description: 'Let the model think more (medium effort)',
    action: 'toggle-bool',
    stateKey: 'isReasoning',
    icon: null,
  },
  {
    command: '/canvas',
    offCommand: '/canvas-off',
    label: 'Canvas',
    description: 'Toggle canvas / document mode',
    action: 'toggle-bool',
    stateKey: 'isCanvas',
    icon: null,
  },
  ...(ENABLE_AGENT_MODE
    ? [
        {
          command: '/agent',
          offCommand: '/agent-off',
          label: 'Agent Mode',
          description: 'Toggle multi-agent orchestration',
          action: 'toggle-bool',
          stateKey: 'isAgentMode',
          icon: null,
        },
      ]
    : []),

  // ── Google Suite ─────────────────────────────────────────
  ...(ENABLE_INTEGRATION_GMAIL
    ? [
        {
          command: '/gmail',
          offCommand: '/gmail-off',
          label: 'Gmail',
          description: 'Enable Gmail tools',
          action: 'toggle-integration',
          appname: 'gmail',
          icon: '/assets/icons/gmail_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_CALENDAR
    ? [
        {
          command: '/gcal',
          offCommand: '/gcal-off',
          label: 'Google Calendar',
          description: 'Enable Google Calendar tools',
          action: 'toggle-integration',
          appname: 'google_calendar',
          icon: '/assets/icons/google_calandar_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_SHEETS
    ? [
        {
          command: '/gsheets',
          offCommand: '/gsheets-off',
          label: 'Google Sheets',
          description: 'Enable Google Sheets tools',
          action: 'toggle-integration',
          appname: 'google_sheets',
          icon: '/assets/icons/google_sheet.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DOCS
    ? [
        {
          command: '/gdocs',
          offCommand: '/gdocs-off',
          label: 'Google Docs',
          description: 'Enable Google Docs tools',
          action: 'toggle-integration',
          appname: 'google_docs',
          icon: '/assets/icons/google_docs_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DRIVE
    ? [
        {
          command: '/gdrive',
          offCommand: '/gdrive-off',
          label: 'Google Drive',
          description: 'Enable Google Drive tools',
          action: 'toggle-integration',
          appname: 'google_drive',
          icon: '/assets/icons/google_drive_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_FORMS
    ? [
        {
          command: '/gforms',
          offCommand: '/gforms-off',
          label: 'Google Forms',
          description: 'Enable Google Forms tools',
          action: 'toggle-integration',
          appname: 'google_forms',
          icon: '/assets/icons/google_form.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_KEEP
    ? [
        {
          command: '/gkeep',
          offCommand: '/gkeep-off',
          label: 'Google Keep',
          description: 'Enable Google Keep tools',
          action: 'toggle-integration',
          appname: 'google_keep',
          icon: '/assets/icons/google_keep.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_DIRECTORY
    ? [
        {
          command: '/gdirectory',
          offCommand: '/gdirectory-off',
          label: 'Google Directory',
          description: 'Enable Google Directory tools',
          action: 'toggle-integration',
          appname: 'google_directory',
          icon: '/assets/icons/google_directory.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_VISION
    ? [
        {
          command: '/gvision',
          offCommand: '/gvision-off',
          label: 'Google Vision',
          description: 'Enable Google Vision tools',
          action: 'toggle-integration',
          appname: 'google_vision',
          icon: '/assets/icons/google_vision.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOOGLE_TASKS
    ? [
        {
          command: '/gtasks',
          offCommand: '/gtasks-off',
          label: 'Google Tasks',
          description: 'Enable Google Tasks tools',
          action: 'toggle-integration',
          appname: 'google_tasks',
          icon: '/assets/icons/google_task.png',
        },
      ]
    : []),

  // ── Microsoft Suite ───────────────────────────────────────
  ...(ENABLE_INTEGRATION_MICROSOFT_OUTLOOK
    ? [
        {
          command: '/outlook',
          offCommand: '/outlook-off',
          label: 'Outlook',
          description: 'Enable Outlook email & calendar tools',
          action: 'toggle-integration',
          appname: 'microsoft_outlook',
          icon: '/assets/icons/outlook.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_TEAMS
    ? [
        {
          command: '/teams',
          offCommand: '/teams-off',
          label: 'Microsoft Teams',
          description: 'Enable Teams messaging tools',
          action: 'toggle-integration',
          appname: 'microsoft_teams',
          icon: '/assets/icons/teams_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_EXCEL
    ? [
        {
          command: '/excel',
          offCommand: '/excel-off',
          label: 'Excel',
          description: 'Enable Excel spreadsheet tools',
          action: 'toggle-integration',
          appname: 'microsoft_excel',
          icon: '/assets/icons/excel_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_WORD
    ? [
        {
          command: '/word',
          offCommand: '/word-off',
          label: 'Word',
          description: 'Enable Word document tools',
          action: 'toggle-integration',
          appname: 'microsoft_word',
          icon: '/assets/icons/ms_words_icon.avif',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_ONENOTE
    ? [
        {
          command: '/onenote',
          offCommand: '/onenote-off',
          label: 'OneNote',
          description: 'Enable OneNote notebook tools',
          action: 'toggle-integration',
          appname: 'microsoft_onenote',
          icon: '/assets/icons/onenote_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_ONEDRIVE
    ? [
        {
          command: '/onedrive',
          offCommand: '/onedrive-off',
          label: 'OneDrive',
          description: 'Enable OneDrive file tools',
          action: 'toggle-integration',
          appname: 'microsoft_onedrive',
          icon: '/assets/icons/onedrive_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_MICROSOFT_TODO
    ? [
        {
          command: '/todo',
          offCommand: '/todo-off',
          label: 'Microsoft Todo',
          description: 'Enable Microsoft Todo tools',
          action: 'toggle-integration',
          appname: 'microsoft_todo',
          icon: '/assets/icons/ms_todo_icon.png',
        },
      ]
    : []),

  // ── Project Management ───────────────────────────────────
  ...(ENABLE_INTEGRATION_JIRA
    ? [
        {
          command: '/jira',
          offCommand: '/jira-off',
          label: 'Jira',
          description: 'Enable Jira tools (issues, sprints…)',
          action: 'toggle-integration',
          appname: 'jira',
          icon: '/assets/icons/jira_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_CONFLUENCE
    ? [
        {
          command: '/confluence',
          offCommand: '/confluence-off',
          label: 'Confluence',
          description: 'Enable Confluence tools',
          action: 'toggle-integration',
          appname: 'confluence',
          icon: '/assets/icons/confluence.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GITHUB
    ? [
        {
          command: '/github',
          offCommand: '/github-off',
          label: 'GitHub',
          description: 'Enable GitHub tools (PRs, issues…)',
          action: 'toggle-integration',
          appname: 'github',
          icon: '/assets/icons/github_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_LINEAR
    ? [
        {
          command: '/linear',
          offCommand: '/linear-off',
          label: 'Linear',
          description: 'Enable Linear tools',
          action: 'toggle-integration',
          appname: 'linear',
          icon: '/assets/icons/linear.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_ASANA
    ? [
        {
          command: '/asana',
          offCommand: '/asana-off',
          label: 'Asana',
          description: 'Enable Asana tools',
          action: 'toggle-integration',
          appname: 'asana',
          icon: '/assets/icons/asana_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_NOTION
    ? [
        {
          command: '/notion',
          offCommand: '/notion-off',
          label: 'Notion',
          description: 'Enable Notion tools',
          action: 'toggle-integration',
          appname: 'notion',
          icon: '/assets/icons/notion_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_WRIKE
    ? [
        {
          command: '/wrike',
          offCommand: '/wrike-off',
          label: 'Wrike',
          description: 'Enable Wrike tools',
          action: 'toggle-integration',
          appname: 'wrike',
          icon: '/assets/icons/wrike_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GITLAB
    ? [
        {
          command: '/gitlab',
          offCommand: '/gitlab-off',
          label: 'GitLab',
          description: 'Enable GitLab tools',
          action: 'toggle-integration',
          appname: 'gitlab',
          icon: '/assets/icons/gitlab_icon.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_PIPEDRIVE
    ? [
        {
          command: '/pipedrive',
          offCommand: '/pipedrive-off',
          label: 'Pipedrive',
          description: 'Enable Pipedrive tools',
          action: 'toggle-integration',
          appname: 'pipedrive',
          icon: '/assets/icons/pipedrive_icon.png',
        },
      ]
    : []),
  {
    command: '/trello',
    offCommand: '/trello-off',
    label: 'Trello',
    description: 'Enable Trello tools',
    action: 'toggle-integration',
    appname: 'trello',
    icon: '/assets/icons/trello_icon.png',
  },
  ...(ENABLE_INTEGRATION_AZURE_DEVOPS
    ? [
        {
          command: '/azure',
          offCommand: '/azure-off',
          label: 'Azure DevOps',
          description: 'Enable Azure DevOps tools',
          action: 'toggle-integration',
          appname: 'azure_devops',
          icon: '/assets/icons/azure_developer_icon.webp',
        },
      ]
    : []),

  // ── Communication ────────────────────────────────────────
  ...(ENABLE_INTEGRATION_SLACK
    ? [
        {
          command: '/slack',
          offCommand: '/slack-off',
          label: 'Slack',
          description: 'Enable Slack messaging tools',
          action: 'toggle-integration',
          appname: 'slack',
          icon: '/assets/icons/slack_icon.svg',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_ZENDESK
    ? [
        {
          command: '/zendesk',
          offCommand: '/zendesk-off',
          label: 'Zendesk',
          description: 'Enable Zendesk support tools',
          action: 'toggle-integration',
          appname: 'zendesk',
          icon: '/assets/icons/zendesk_icon.svg',
        },
      ]
    : []),

  // ── CRM & Business ───────────────────────────────────────
  ...(ENABLE_INTEGRATION_HUBSPOT
    ? [
        {
          command: '/hubspot',
          offCommand: '/hubspot-off',
          label: 'HubSpot',
          description: 'Enable HubSpot CRM tools',
          action: 'toggle-integration',
          appname: 'hubspot',
          icon: null,
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_ZOHO_CRM
    ? [
        {
          command: '/zoho',
          offCommand: '/zoho-off',
          label: 'Zoho CRM',
          description: 'Enable Zoho CRM tools',
          action: 'toggle-integration',
          appname: 'zoho_crm',
          icon: '/assets/icons/zoho_crm.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_GOHIGHLEVEL
    ? [
        {
          command: '/ghl',
          offCommand: '/ghl-off',
          label: 'GoHighLevel',
          description: 'Enable GoHighLevel tools',
          action: 'toggle-integration',
          appname: 'gohighlevel',
          icon: null,
        },
      ]
    : []),

  // ── Commerce & Payments ──────────────────────────────────
  ...(ENABLE_INTEGRATION_SHIPROCKET
    ? [
        {
          command: '/shiprocket',
          offCommand: '/shiprocket-off',
          label: 'Shiprocket',
          description: 'Enable Shiprocket shipping tools',
          action: 'toggle-integration',
          appname: 'shiprocket',
          icon: '/assets/icons/shiprocket_logo.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_RAZORPAY
    ? [
        {
          command: '/razorpay',
          offCommand: '/razorpay-off',
          label: 'Razorpay',
          description: 'Enable Razorpay payment tools',
          action: 'toggle-integration',
          appname: 'razorpay',
          icon: '/assets/icons/razorpay.png',
        },
      ]
    : []),

  // ── Social & Media ───────────────────────────────────────
  ...(ENABLE_INTEGRATION_QUICKBOOKS
    ? [
        {
          command: '/quickbooks',
          offCommand: '/quickbooks-off',
          label: 'QuickBooks',
          description: 'Enable QuickBooks tools',
          action: 'toggle-integration',
          appname: 'quickbooks',
          icon: '/assets/icons/quickbooks.png',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_INSTAGRAM
    ? [
        {
          command: '/instagram',
          offCommand: '/instagram-off',
          label: 'Instagram',
          description: 'Enable Instagram tools',
          action: 'toggle-integration',
          appname: 'instagram',
          icon: '/assets/icons/instagram_icon.jpg',
        },
      ]
    : []),
  ...(ENABLE_INTEGRATION_YOUTUBE
    ? [
        {
          command: '/youtube',
          offCommand: '/youtube-off',
          label: 'YouTube',
          description: 'Enable YouTube tools',
          action: 'toggle-integration',
          appname: 'youtube',
          icon: '/assets/icons/youtube_icon.png',
        },
      ]
    : []),

  // ── MCP (panel-only, no on-send) ─────────────────────────
  {
    command: '/mcp',
    offCommand: null,
    label: 'MCP Connections',
    description: 'Open MCP connections & tools panel',
    action: 'open-panel',
    panelType: 'mcp',
    icon: null,
  },
];
